<!-- BondTypeConditionComponent.vue -->
<template>
  <div>
    <!-- 채권 유형 선택 -->
    <div class="grid grid-cols-4 gap-2 mb-4">
      <button v-for="type in bondTypes" :key="type.id" @click="selectBondType(type)" :disabled="isLoading" :class="[
        'px-2.5 py-1.5 rounded-md text-sm font-medium transition-colors',
        bondType.bondTypeId === type.id
          ? 'bg-gray-800 text-white'
          : isLoading
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
      ]">
        {{ type.name }}
      </button>
    </div>

    <!-- 필터 드롭다운 -->
    <div v-if="bondType.bondTypeId" class="space-y-3">
      <div class="flex gap-2">
        <!-- 그룹 필터 드롭다운 -->
        <div class="relative flex-1">
          <button @click="toggleGroupDropdown"
            class="dropdown-button w-full px-3 py-2 text-sm border rounded-md bg-white flex items-center justify-between"
            :class="bondType.selectedGroupId
              ? 'border-gray-500 text-gray-700'
              : 'border-gray-300 text-gray-600'
              ">
            <span>{{ bondType.selectedGroupName || '그룹 전체' }}</span>
            <svg class="w-4 h-4" :class="showGroupDropdown ? 'rotate-180' : ''" fill="none" stroke="currentColor"
              viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          <!-- 그룹 목록 -->
          <div v-if="showGroupDropdown"
            class="dropdown-content absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
            <div class="p-1">
              <button @click="selectGroup(null)"
                class="w-full px-3 py-2 text-sm text-left hover:bg-gray-50 rounded flex items-center justify-between">
                <span>그룹 전체</span>
                <svg v-if="!bondType.selectedGroupId" class="w-4 h-4 text-blue-600" fill="currentColor"
                  viewBox="0 0 20 20">
                  <path
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" />
                </svg>
              </button>
              <button v-for="group in bondGroups" :key="group.id" @click="selectGroup(group)"
                class="w-full px-3 py-2 text-sm text-left hover:bg-gray-50 rounded flex items-center justify-between">
                <span>{{ group.name }}</span>
                <svg v-if="bondType.selectedGroupId === group.id" class="w-4 h-4 text-blue-600" fill="currentColor"
                  viewBox="0 0 20 20">
                  <path
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <!-- 등급 필터 드롭다운 -->
        <div class="relative flex-1">
          <button @click="toggleRatingDropdown"
            class="dropdown-button w-full px-3 py-2 text-sm border rounded-md bg-white flex items-center justify-between"
            :class="!isAllSelected
              ? 'border-gray-500 text-gray-700'
              : 'border-gray-300 text-gray-600'
              ">
            <span>{{ getRatingDisplay }}</span>
            <svg class="w-4 h-4" :class="showRatingDropdown ? 'rotate-180' : ''" fill="none" stroke="currentColor"
              viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          <!-- 등급 선택 패널 -->
          <div v-if="showRatingDropdown"
            class="dropdown-content absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg">
            <div class="p-2 space-y-2">
              <button @click="toggleAllRatings"
                class="w-full px-3 py-2 text-sm text-left rounded flex items-center justify-between border transition-colors"
                :class="isAllSelected
                  ? 'border-gray-500 bg-gray-50 text-gray-700'
                  : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                  ">
                <span>{{ isAllSelected ? '전체 해제' : '전체 선택' }}</span>
                <svg v-if="isAllSelected" class="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" />
                </svg>
              </button>
              <div class="grid grid-cols-2 gap-1.5">
                <button v-for="rating in bondGrades" :key="rating" @click="toggleRating(rating)"
                  class="px-3 py-2 text-sm rounded flex items-center justify-between border transition-colors" :class="bondType.selectedRatings.includes(rating)
                    ? 'border-gray-500 bg-gray-50 text-gray-700'
                    : 'hover:border-gray-400 hover:bg-gray-50'
                    ">
                  <span>{{ rating }}</span>
                  <svg v-if="bondType.selectedRatings.includes(rating)" class="w-4 h-4 text-blue-600"
                    fill="currentColor" viewBox="0 0 20 20">
                    <path
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import bondService from '@/service/bondService';

export default {
  name: 'BondTypeConditionComponent',
  emits: ['bondGroupsUpdated'],
  setup(props, { emit }) {
    const store = useStore();
    const isLoading = ref(false);
    const showGroupDropdown = ref(false);
    const showRatingDropdown = ref(false);

    const bondGrades = [
      'AAA',
      'AA+',
      'AA',
      'AA-',
      'A+',
      'A',
      'A-',
      'BBB+',
      'BBB',
      'BBB-',
    ];

    const bondTypes = [
      { id: 'PUBLIC', name: '공사채' },
      { id: 'BANK', name: '은행채' },
      { id: 'CREDIT_FINANCE', name: '여전채' },
      { id: 'COMPANY', name: '회사채' },
    ];

    // Store에서 상태 가져오기
    const bondType = computed(() => store.state.filter.bondType);
    const bondGroups = computed(() => store.state.filter.bondGroups);

    const isAllSelected = computed(
      () => bondType.value.selectedRatings.length === bondGrades.length
    );

    const getRatingDisplay = computed(() => {
      if (bondType.value.selectedRatings.length === bondGrades.length)
        return '등급 전체';
      if (bondType.value.selectedRatings.length === 0) return '등급 선택';
      return `${bondType.value.selectedRatings.length}개 선택됨`;
    });

    const fetchBondGroups = async bondTypeId => {
      try {
        isLoading.value = true;
        const response = await bondService.getBondGroups(bondTypeId);

        // 각 그룹에 issuerIds 필드 추가 (issuers 배열에서 id들을 추출)
        const processedGroups = response.map(group => ({
          ...group,
          issuerIds: group.issuers ? group.issuers.map(issuer => issuer.id) : []
        }));

        store.dispatch('filter/setBondGroups', processedGroups);
        emit('bondGroupsUpdated', processedGroups);
      } catch (error) {
        console.error('Error fetching bond groups:', error);
        store.dispatch('filter/setBondGroups', []);
        emit('bondGroupsUpdated', []);
      } finally {
        setTimeout(() => {
          isLoading.value = false;
        }, 500);
      }
    };

    const selectBondType = async type => {
      if (isLoading.value) return;

      store.dispatch('filter/setBondType', {
        bondTypeId: type.id,
        bondTypeName: type.name,
        selectedGroupId: null,
        selectedGroupName: '',
        issuerIds: null,
        selectedRatings: [...bondGrades],
        filterMode: null,
      });

      await fetchBondGroups(type.id);
    };

    const selectGroup = group => {
      if (isLoading.value) return;

      store.dispatch('filter/setBondType', {
        ...bondType.value,
        selectedGroupId: group?.id || null,
        selectedGroupName: group?.name || '',
        issuerIds: group?.issuerIds || null,
        filterMode: 'group',
      });
      showGroupDropdown.value = false;
    };

    const toggleAllRatings = () => {
      const newRatings = isAllSelected.value ? [] : [...bondGrades];
      store.dispatch('filter/setBondType', {
        ...bondType.value,
        selectedRatings: newRatings,
        filterMode: 'rating',
      });
    };

    const toggleRating = rating => {
      const currentRatings = [...bondType.value.selectedRatings];
      const index = currentRatings.indexOf(rating);
      if (index === -1) {
        currentRatings.push(rating);
      } else {
        currentRatings.splice(index, 1);
      }

      store.dispatch('filter/setBondType', {
        ...bondType.value,
        selectedRatings: currentRatings,
        filterMode: 'rating',
      });
    };

    const toggleGroupDropdown = () => {
      showGroupDropdown.value = !showGroupDropdown.value;
      showRatingDropdown.value = false;
    };

    const toggleRatingDropdown = () => {
      showRatingDropdown.value = !showRatingDropdown.value;
      showGroupDropdown.value = false;
    };

    // Close dropdowns when clicking outside
    onMounted(() => {
      document.addEventListener('click', e => {
        const target = e.target;
        if (
          !target.closest('.dropdown-button') &&
          !target.closest('.dropdown-content')
        ) {
          showGroupDropdown.value = false;
          showRatingDropdown.value = false;
        }
      });
    });

    return {
      bondTypes,
      bondGroups,
      bondGrades,
      bondType,
      selectBondType,
      selectGroup,
      isLoading,
      showGroupDropdown,
      showRatingDropdown,
      toggleGroupDropdown,
      toggleRatingDropdown,
      getRatingDisplay,
      toggleRating,
      isAllSelected,
      toggleAllRatings,
    };
  },
};
</script>

<style scoped>
/* 스크롤바 스타일링 */
.overflow-y-auto::-webkit-scrollbar {
  width: 4px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: #cbd5e1;
  border-radius: 4px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background-color: #94a3b8;
}
</style>
